/**
 * Simple helper to check if all questions in a list have been picked
 * @param {Array<string>} questionIds - Array of question IDs to check
 * @param {Object} questionsObject - Questions object from game state (keyed by ID)
 * @returns {boolean} - True if all questions are picked
 */
export const areAllQuestionsPicked = (questionIds, questions) => {
  if (!questionIds || questionIds.length === 0) {
    return false;
  }
  if (!questions) return false;

  // Support both array (Redux entities) and object (game doc keyed by ID)
  if (Array.isArray(questions)) {
    return questionIds.every(id => questions.find(q => q.id === id)?.picked === true);
  }
  return questionIds.every(id => questions[id]?.picked === true);
};

/**
 * Extracts all question IDs for a given round from the game object
 * @param {Object} gameObject - The game object from Redux state
 * @param {string} round - The round identifier (practice, round1, round2)
 * @returns {Array<string>} - Array of question IDs for this round
 */
export const extractQuestionIdsForRound = (gameObject, round) => {
  const questionIds = [];
  const categories = gameObject?.categories?.[round];

  if (!categories) {
    return questionIds;
  }

  // Categories can be an array or object
  const categoryArray = Array.isArray(categories)
    ? categories
    : Object.values(categories);

  // Each category has questions numbered 1-5
  for (const category of categoryArray) {
    if (!category) continue;

    for (let i = 1; i <= 5; i++) {
      const questionId = category[i];
      if (questionId) {
        questionIds.push(questionId);
      }
    }
  }

  return questionIds;
};

/**
 * Builds the round progression dynamically from the categories object.
 * Games can have variable numbers of numbered rounds (round1, round2, round3, ...).
 * Order: practice, round1, round2, ..., roundN, final
 * @param {Object} categories - The categories object from the game
 * @returns {Array<string>} - Ordered array of round keys that exist
 */
export const buildRoundProgression = (categories) => {
  if (!categories) return [];

  const progression = [];

  if (categories.practice) progression.push('practice');

  // Collect all roundN keys and sort numerically
  const numberedRounds = Object.keys(categories)
    .filter(key => /^round\d+$/.test(key))
    .sort((a, b) => parseInt(a.replace('round', '')) - parseInt(b.replace('round', '')));
  progression.push(...numberedRounds);

  if (categories.final) progression.push('final');

  return progression;
};

/**
 * Determines the next round in the game progression dynamically
 * Checks which categories actually exist in the game to support configurable rounds
 * @param {string} currentRound - The current round identifier
 * @param {Object} categories - The categories object from the game
 * @returns {string|null} - The next round identifier, or null if no more rounds
 */
export const determineNextRound = (currentRound, categories) => {
  const roundProgression = buildRoundProgression(categories);

  const currentIndex = roundProgression.indexOf(currentRound);
  if (currentIndex === -1) {
    return null; // Unknown round
  }

  if (currentIndex + 1 < roundProgression.length) {
    return roundProgression[currentIndex + 1];
  }

  return null; // No more rounds
};

/**
 * Returns a human-readable display name for a round key
 * @param {string} round - The round key (e.g. 'practice', 'round1', 'round3', 'final')
 * @returns {string}
 */
export const getRoundDisplayName = (round) => {
  if (!round) return '';
  if (round === 'practice') return 'Practice Round';
  if (round === 'final') return 'Final Jeopardy';
  const match = round.match(/^round(\d+)$/);
  if (match) return `Round ${match[1]}`;
  return round;
};
